import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/components/FormSection';
import { ErrorBanner } from '@/components';
import { PrimaryButton } from '@/components/PrimaryButton';
import { OptionSelector, type OptionSelectorOption } from '@/components/OptionSelector';
import { ContactFields } from './ContactFields';
import { SelectExistingContact } from './SelectExistingContact';
import type { UserRole } from '../../api/employees';
import type { JoinRequestInfo } from '../../types';
import type { EmployeeFormData } from './CreateEmployeeForm';

export type ContactMode = 'create' | 'select';

interface JoinRequestFormContentProps {
    formData: EmployeeFormData;
    errors: {
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        email?: string;
        roleId?: string;
        general?: string;
    };
    roles?: UserRole[];
    rolesLoading: boolean;
    handleChange: (field: keyof EmployeeFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleToggleMainContact: () => void;
    handleDecline: () => void;
    joinRequest: JoinRequestInfo;
    isFormValid: boolean;
    isAccepting: boolean;
    isDeclining: boolean;
    companyId: string;
    contactMode: ContactMode;
    onContactModeChange: (mode: ContactMode) => void;
    selectedContactId: string | null;
    onSelectedContactChange: (contactId: string | null) => void;
}

export function JoinRequestFormContent({
    formData,
    errors,
    roles,
    rolesLoading,
    handleChange,
    handleToggleMainContact,
    handleDecline,
    joinRequest,
    isFormValid,
    isAccepting,
    isDeclining,
    companyId,
    contactMode,
    onContactModeChange,
    selectedContactId,
    onSelectedContactChange,
}: JoinRequestFormContentProps) {
    const { t } = useTranslation();

    const contactModeOptions: OptionSelectorOption<ContactMode>[] = useMemo(
        () => [
            {
                value: 'create' as ContactMode,
                title: t('employees.createForm.contactMode.createNew.title'),
                description: t('employees.createForm.contactMode.createNew.description'),
            },
            {
                value: 'select' as ContactMode,
                title: t('employees.createForm.contactMode.selectExisting.title'),
                description: t('employees.createForm.contactMode.selectExisting.description'),
            },
        ],
        [t]
    );

    const isAcceptEnabled =
        contactMode === 'create' ? isFormValid : selectedContactId !== null;

    return (
        <>

            <FormSection icon="contact" title={t('employees.createForm.sections.contact')}>
                <OptionSelector<ContactMode>
                    options={contactModeOptions}
                    value={contactMode}
                    onChange={onContactModeChange}
                />


                {contactMode === 'create' ? (
                    <ContactFields
                        formData={formData}
                        errors={errors}
                        roles={roles}
                        rolesLoading={rolesLoading}
                        handleChange={handleChange}
                        handleToggleMainContact={handleToggleMainContact}
                    />
                ) : (
                    <SelectExistingContact
                        companyId={companyId}
                        selectedContactId={selectedContactId}
                        onSelect={onSelectedContactChange}
                    />
                )}
            </FormSection>


            <div className="mt-1" />

            <FormSection icon="user" title={t('employees.createForm.sections.user')}>
                <div className="rounded-lg px-4">
                    <div className="space-y-1">
                        <div>
                            <span className="text-xs text-muted-foreground">{t('employees.createForm.joinRequest.name')}</span>
                            <p className="text-sm font-medium text-onsurface">{joinRequest.userName}</p>
                        </div>
                        <div>
                            <span className="text-xs text-muted-foreground">{t('employees.createForm.joinRequest.email')}</span>
                            <p className="text-sm font-medium text-onsurface">{joinRequest.userEmail}</p>
                        </div>
                    </div>
                </div>
            </FormSection>

            <ErrorBanner hasError={errors.general !== undefined}>
                {errors.general}
            </ErrorBanner>

            <div className="pt-4 border-t border-border">
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleDecline}
                        disabled={isDeclining || isAccepting}
                        className="flex-1 py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-sm border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none focus:ring-2 focus:ring-border disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                        {isDeclining && (
                            <span className="animate-spin inline-block size-4 border-2 border-current border-t-transparent rounded-full" />
                        )}
                        {t('employees.createForm.decline')}
                    </button>
                    <PrimaryButton
                        type="submit"
                        className="flex-1"
                        isLoading={isAccepting}
                        disabled={isAccepting || isDeclining || !isAcceptEnabled}
                    >
                        {t('employees.createForm.accept')}
                    </PrimaryButton>
                </div>
            </div>
        </>
    );
}
